import React, { Fragment } from 'react';
import axios from 'axios';
import { Column, Cell } from 'react-table';
import { useAuth } from '../../common/auth/use-auth';
import { Project, UserProjectPermission, User, ApiResult } from '../../common/dataTypes/Jetstream';
import { ConfirmAction, ToolButton, Breakpoint, Small, Medium, Card, Layout, LayoutItem, JetTable, EditPanel, EditPanelState, useForm, Formic } from '../../jet-ui';

import './ProjectPermissions.css';

type PermissionForm = { userId: string; canRun: boolean };

interface ProjectWithPermissions extends Project {
  permissions: UserProjectPermission[];
}

/**
 * A component for managing user run-permissions on projects.
 * Accessible from the Admin page.
 */
export const ProjectPermissions: React.FC = () => {
  const [projects, setProjects] = React.useState<ProjectWithPermissions[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedProject, setSelectedProject] = React.useState<ProjectWithPermissions | null>(null);
  const [editPanelState, setEditPanelState] = React.useState<EditPanelState<PermissionForm>>({ value: {}, open: false });
  const [deleteTarget, setDeleteTarget] = React.useState<ProjectWithPermissions | null>(null);

  const auth = useAuth();
  const Form = useForm(editPanelState.value);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    const [projectsRes, usersRes] = await Promise.all([
      axios.get<ApiResult<Project[]>>('/api/projects', auth.getRequestConfig()),
      axios.get<ApiResult<User[]>>('/api/users', auth.getRequestConfig()),
    ]);
    setUsers(usersRes.data.data);

    const withPerms = await Promise.all(projectsRes.data.data.map(async p => {
      const permsRes = await axios.get<ApiResult<UserProjectPermission[]>>(`/api/projects/${p.projectId}/permissions`, auth.getRequestConfig());
      return { ...p, permissions: permsRes.data.data };
    }));

    setProjects(withPerms);
    setLoading(false);
  }, [auth]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRevoke = React.useCallback(async (perm: UserProjectPermission, confirmed: boolean) => {
    if (!confirmed || !selectedProject) return;
    await axios.delete<ApiResult<any>>(`/api/projects/${selectedProject.projectId}/permissions/${encodeURIComponent(perm.userId)}`, auth.getRequestConfig());
    await fetchAll();
  }, [selectedProject, fetchAll, auth]);

  const handleSave = React.useCallback(async (form: Partial<Formic<PermissionForm>>) => {
    if (!selectedProject) return;
    await axios.post<ApiResult<any>>(`/api/projects/${selectedProject.projectId}/permissions`, { userId: form.userId, canRun: true }, auth.getRequestConfig());
    setEditPanelState(prev => ({ ...prev, open: false, status: 'Permission granted.' }));
    await fetchAll();
  }, [selectedProject, fetchAll, auth]);

  const handleDeleteProject = React.useCallback(async () => {
    if (!deleteTarget) return;
    await axios.delete<ApiResult<any>>(`/api/projects/${deleteTarget.projectId}`, auth.getRequestConfig());
    setDeleteTarget(null);
    if (selectedProject?.projectId === deleteTarget.projectId) {
      setSelectedProject(null);
    }
    await fetchAll();
  }, [deleteTarget, selectedProject, fetchAll, auth]);

  const permColumns = React.useMemo<Column<UserProjectPermission>[]>(() => [
    {
      id: 'actions', width: 60,
      Cell: (cell: Cell<UserProjectPermission>) => (
        <ConfirmAction value={cell.row.original} icon="delete" onAction={handleRevoke} title="Revoke Permission">
          Revoke run permission for {cell.row.original.userId}?
        </ConfirmAction>
      )
    },
    { accessor: 'userId', Header: 'User ID' },
    { accessor: 'userName', Header: 'Name' },
    { id: 'canRun', Header: 'Can Run', Cell: () => <span>✓</span> },
  ], [handleRevoke]);

  const projectColumns = React.useMemo<Column<ProjectWithPermissions>[]>(() => [
    {
      id: 'actions', width: 60,
      Cell: (cell: Cell<ProjectWithPermissions>) => (
        <ToolButton icon="manage_accounts" onClick={() => setSelectedProject(cell.row.original)} collapse />
      )
    },
    { accessor: 'name', Header: 'Project' },
    { id: 'flowCount', Header: 'Flows', Cell: (cell: Cell<ProjectWithPermissions>) => <span>{cell.row.original.flowCount ?? 0}</span> },
    { id: 'userCount', Header: 'Users with Run Access', Cell: (cell: Cell<ProjectWithPermissions>) => <span>{cell.row.original.permissions.length}</span> },
    {
      id: 'delete', width: 60,
      Cell: (cell: Cell<ProjectWithPermissions>) => {
        const p = cell.row.original;
        return (p.flowCount ?? 0) === 0
          ? <ToolButton icon="close" onClick={() => setDeleteTarget(p)} collapse title="Delete project" />
          : null;
      }
    },
  ], []);

  return (
    <div>
      <h3>Project Permissions</h3>
      <p>All users can <strong>view</strong> all projects by default. Grant <strong>run</strong> permission below.</p>

      <JetTable data={projects} columns={projectColumns} loading={loading} rowKey={p => String(p.projectId)} />

      {selectedProject && (
        <div style={{ marginTop: 24 }}>
          <h4>Run permissions for: <em>{selectedProject.name}</em></h4>
          <ToolButton icon="add" onClick={() => setEditPanelState({ value: {}, open: true })}>
            Grant Run Access
          </ToolButton>
          <JetTable
            data={selectedProject.permissions}
            columns={permColumns}
            loading={loading}
            rowKey={p => String(p.permissionId)}
          />
        </div>
      )}

      <EditPanel<PermissionForm>
        state={editPanelState}
        addLabel="Grant Permission"
        modelLabel="Permission"
        onSave={handleSave}
        form={Form}
      >
        <Form.Input name="userId" placeholder="User ID" required />
      </EditPanel>

      {deleteTarget && (
        <div className="ProjectPermissions__DeleteOverlay">
          <div className="ProjectPermissions__DeleteDialog">
            <p>Confirm to delete project <strong>{deleteTarget.name}</strong>?</p>
            <div className="ProjectPermissions__DeleteActions">
              <button className="ProjectPermissions__DeleteConfirm" onClick={handleDeleteProject}>Confirm</button>
              <button className="ProjectPermissions__DeleteCancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
